import type { BlogCluster, BlogPost, BlogPostSummary } from "./blogTypes";

/**
 * Dados que o SSR do blog já buscou, entregues ao SPA junto com o HTML.
 *
 * Por que existe: o `main.tsx` monta o app com `createRoot` — ele SUBSTITUI o
 * HTML do SSR em vez de hidratá-lo. Sem estes dados, o React apagava o post
 * pronto, mostrava o esqueleto e buscava o mesmo post na API de novo. No
 * renderizador do Google essa segunda volta falhava às vezes ("Outro erro" ao
 * baixar o chunk ou a API), e a página caía na tela de erro: "Erro soft 404"
 * no Search Console, com o post recusado para indexação.
 *
 * Com os dados aqui, o primeiro render do cliente já é o post completo.
 */

export const BLOG_SSR_DATA_ID = "blog-ssr-data";

export type BlogSsrData =
  | { kind: "post"; slug: string; post: BlogPost }
  | {
      kind: "cluster";
      slug: string;
      hub: { cluster: BlogCluster; posts: BlogPostSummary[] };
    };

/**
 * `<script type="application/json">` para o `<head>` do SSR. O `<` vira
 * `\u003c` para que um `</script>` dentro do conteúdo do post não feche a tag.
 */
export const renderBlogSsrDataScript = (data: BlogSsrData): string =>
  `<script id="${BLOG_SSR_DATA_ID}" type="application/json">${JSON.stringify(
    data
  ).replace(/</g, "\\u003c")}</script>`;

/** Lê o que o SSR entregou nesta página; `null` fora do blog ou se ilegível. */
export const readBlogSsrData = (): BlogSsrData | null => {
  if (typeof document === "undefined") return null;
  const element = document.getElementById(BLOG_SSR_DATA_ID);
  if (!element?.textContent) return null;
  try {
    return JSON.parse(element.textContent) as BlogSsrData;
  } catch {
    return null;
  }
};
